import { useEffect, useMemo, useState } from "react";
import { ClipboardPen, Lock, ShieldAlert, X } from "lucide-react";
import Input from "./common/Input";
import Select from "./common/Select";
import { getSavedTopologies } from "../api/services/topologyService";

function normalizeSavedTopologiesList(savedRes) {
  if (Array.isArray(savedRes?.saved_topologies)) {
    return savedRes.saved_topologies;
  }
  if (Array.isArray(savedRes?.topologies)) {
    return savedRes.topologies;
  }
  if (Array.isArray(savedRes)) {
    return savedRes;
  }
  return [];
}

const FRAMEWORK_OPTIONS = [
  { value: "", label: "Select a framework" },
  { value: "NICE", label: "NICE" },
  { value: "Scwyf", label: "Scwyf" },
  { value: "ECSF", label: "ECSF" },
];

const DIFFICULTY_OPTIONS = [
  { value: "", label: "Select difficulty" },
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
  { value: "legendary", label: "Legendary" },
];

const DURATION_OPTIONS = [
  { value: "", label: "Select duration" },
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "120", label: "2 hours" },
  { value: "240", label: "4 hours" },
];

const ATTACK_CHAIN_OPTIONS = [
  { value: "", label: "Select attack chain" },
  { value: "multi", label: "Multi-stage attack chain" },
  { value: "single", label: "One and done" },
];

const AI_COMPANION_OPTIONS = [
  { value: "", label: "Select AI companion mode" },
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
];

const CONTROL_OPTIONS = ["SIEM", "EDR", "Firewall", "IDS", "WAF"];

const NOISE_OPTIONS = [
  { value: "", label: "Select noise mode" },
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
];

const PERSPECTIVE_OPTIONS = [
  { value: "", label: "Select perspective" },
  { value: "blue", label: "Blue Team" },
  { value: "red", label: "Red Team" },
  { value: "purple", label: "Purple Team" },
];

function LockedFieldOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-start justify-end pr-3 pt-1">
      <div className="flex items-center gap-1 rounded-md border border-(--color-border) bg-(--color-bg-card)/90 px-2 py-1 text-[10px] font-medium text-(--color-text-muted) backdrop-blur-sm">
        <Lock className="h-3 w-3" />
      </div>
    </div>
  );
}

export default function EmergingThreatRequestModal({
  open,
  onClose,
  onSubmit,
}) {
  const [cveTitle, setCveTitle] = useState("");
  const [description, setDescription] = useState("");
  const [framework, setFramework] = useState("");
  const [selectedTopologyId, setSelectedTopologyId] = useState("");
  const [savedTopologies, setSavedTopologies] = useState([]);
  const [isLoadingTopologies, setIsLoadingTopologies] = useState(false);
  const [topologyLoadError, setTopologyLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setCveTitle("");
      setDescription("");
      setFramework("");
      setSelectedTopologyId("");
      setSavedTopologies([]);
      setTopologyLoadError("");
      setSubmitError("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isActive = true;

    const loadSavedTopologies = async () => {
      try {
        setIsLoadingTopologies(true);
        setTopologyLoadError("");
        const response = await getSavedTopologies();
        if (!isActive) {
          return;
        }
        setSavedTopologies(normalizeSavedTopologiesList(response));
      } catch (error) {
        if (!isActive) {
          return;
        }
        setSavedTopologies([]);
        setTopologyLoadError(
          error?.message || "Failed to load saved topologies.",
        );
      } finally {
        if (isActive) {
          setIsLoadingTopologies(false);
        }
      }
    };

    loadSavedTopologies();

    return () => {
      isActive = false;
    };
  }, [open]);

  const topologyOptions = useMemo(() => {
    const base = [{ value: "", label: "Select a saved topology" }];
    const mapped = savedTopologies.map((item, index) => ({
      value: String(item.id ?? item.topology_id ?? item.thread_id ?? index),
      label: item.name ?? item.title ?? `Saved Topology ${index + 1}`,
    }));
    return [...base, ...mapped];
  }, [savedTopologies]);

  if (!open) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!cveTitle.trim() || !description.trim()) {
      return;
    }

    const selectedTopology = savedTopologies.find((item, index) => {
      const itemId = String(
        item.id ?? item.topology_id ?? item.thread_id ?? index,
      );
      return itemId === selectedTopologyId;
    });

    const topologyId = selectedTopology
      ? String(
          selectedTopology.id ??
            selectedTopology.topology_id ??
            selectedTopology.thread_id ??
            selectedTopologyId,
        )
      : selectedTopologyId;

    setIsSubmitting(true);
    setSubmitError("");

    try {
      await onSubmit?.({
        cveTitle: cveTitle.trim(),
        description: description.trim(),
        framework: framework.trim(),
        savedTopologyId: selectedTopologyId || null,
        savedTopologyName:
          selectedTopology?.name ?? selectedTopology?.title ?? null,
        topology_id: topologyId || null,
      });
      onClose();
    } catch (error) {
      setSubmitError(error?.message || "Failed to capture request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-1000 flex items-center justify-center bg-black/75 px-4 py-8 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-180 flex-col rounded-sm border border-(--color-border) bg-(--color-bg-card) shadow-[0_24px_60px_rgba(0,0,0,0.28)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-(--color-border) px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-(--color-primary)/20 bg-(--color-primary)/10">
              <ShieldAlert className="h-5 w-5 text-(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-(--color-text-primary)">
                Request a Custom Box
              </h3>
              <p className="text-xs text-(--color-text-muted)">
                Capture the custom version details here.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center text-(--color-text-muted) transition-colors hover:text-(--color-primary)"
            aria-label="Close"
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          className="flex-1 space-y-4 overflow-y-auto px-6 py-6"
          onSubmit={handleSubmit}
        >
          <Input
            label="CVE Title"
            name="cveTitle"
            value={cveTitle}
            onChange={(event) => setCveTitle(event.target.value)}
            placeholder="e.g. CVE-2021-41774 - Apache Path Traversal"
            required
            icon={ClipboardPen}
          />

          <div>
            <label className="mb-2 block text-sm font-medium text-(--color-text-secondary)">
              Description
              <span className="required-asterisk" />
            </label>
            <textarea
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Describe the box you want created for this custom version."
              required
              className="w-full rounded-sm border-2 border-(--color-border) bg-(--color-bg-card) px-4 py-3 text-sm text-(--color-text-primary) placeholder-(--color-text-muted) focus:border-(--color-primary) focus:outline-none focus:shadow-[0_0_8px_var(--color-shadow-primary)]"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Select
              label="Saved Topologies"
              name="saved_topology"
              value={selectedTopologyId}
              onChange={(event) => setSelectedTopologyId(event.target.value)}
              options={topologyOptions}
              placeholder={
                isLoadingTopologies
                  ? "Loading saved topologies..."
                  : "Select a saved topology"
              }
            />

            <Select
              label="Framework"
              name="framework"
              value={framework}
              onChange={(event) => setFramework(event.target.value)}
              options={FRAMEWORK_OPTIONS}
              placeholder="Select a framework"
            />
          </div>

          {topologyLoadError && (
            <p className="-mt-2 text-xs text-red-400">{topologyLoadError}</p>
          )}

          {/* Locked / coming-soon fields — not submitted to backend */}
          <div className="pt-2">
            <div className="mb-3 flex items-center gap-2 border-t border-dashed border-(--color-border) pt-4">
              <Lock className="h-3.5 w-3.5 text-(--color-text-muted)" />
              <p className="text-xs font-medium uppercase tracking-wider text-(--color-text-muted)">
                Advanced options
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 opacity-60 md:grid-cols-2">
              <div className="relative">
                <Select
                  label="Difficulty Level"
                  name="difficulty"
                  value=""
                  onChange={() => {}}
                  options={DIFFICULTY_OPTIONS}
                  placeholder="Select difficulty"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative">
                <Select
                  label="Estimated Duration"
                  name="duration"
                  value=""
                  onChange={() => {}}
                  options={DURATION_OPTIONS}
                  placeholder="Select duration"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative">
                <Input
                  label="Specific MITRE TTPs"
                  name="ttps"
                  value=""
                  onChange={() => {}}
                  placeholder="e.g. T1059, T1110, T1486"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative">
                <Select
                  label="Attack Chain"
                  name="attackChain"
                  value=""
                  onChange={() => {}}
                  options={ATTACK_CHAIN_OPTIONS}
                  placeholder="Select attack chain"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative md:col-span-2">
                <Select
                  label="AI Companion"
                  name="aiCompanion"
                  value=""
                  onChange={() => {}}
                  options={AI_COMPANION_OPTIONS}
                  placeholder="Select AI companion mode"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-(--color-text-secondary)">
                  Specific Controls
                </label>
                <div className="flex flex-wrap gap-2 rounded-sm border-2 border-(--color-border) bg-(--color-bg-card) px-4 py-3">
                  {CONTROL_OPTIONS.map((control) => (
                    <label
                      key={control}
                      className="flex cursor-not-allowed items-center gap-2 rounded-md border border-(--color-border) px-3 py-1.5 text-xs text-(--color-text-muted)"
                    >
                      <input
                        type="checkbox"
                        disabled
                        className="h-3 w-3 cursor-not-allowed"
                      />
                      {control}
                    </label>
                  ))}
                </div>
                <LockedFieldOverlay />
              </div>

              <div className="relative">
                <Select
                  label="Legitimate User Noise"
                  name="userNoise"
                  value=""
                  onChange={() => {}}
                  options={NOISE_OPTIONS}
                  placeholder="Select noise mode"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative">
                <Select
                  label="Attack Perspective"
                  name="perspective"
                  value=""
                  onChange={() => {}}
                  options={PERSPECTIVE_OPTIONS}
                  placeholder="Select perspective"
                  disabled
                />
                <LockedFieldOverlay />
              </div>
            </div>
          </div>

          {submitError && <p className="text-sm text-red-400">{submitError}</p>}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-sm border border-(--color-border) px-4 py-2 text-sm font-semibold text-(--color-text-muted) transition hover:border-(--color-primary)/40 hover:text-(--color-primary) disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-sm border border-(--color-primary) bg-(--color-primary) px-4 py-2 text-sm font-semibold text-(--color-text-on-primary) transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Saving..." : "Save Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
